import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { TagList } from '../components/TagBadge';
import { fmtDate, fmtMinutes } from '../utils/dates';
import { printReport } from '../utils/printReport';

const STATUS_AR = {
  pending: 'معلّقة', in_progress: 'قيد التنفيذ', waiting_approval: 'بانتظار الاعتماد',
  approved: 'معتمدة', revision: 'مراجعة', completed: 'مكتملة', cancelled: 'ملغاة',
};

const ALL_COLUMNS = [
  { key: 'title', label: 'عنوان المهمة', default: true },
  { key: 'project_title', label: 'المشروع', default: true },
  { key: 'sponsor_name', label: 'الداعم', default: false },
  { key: 'assignee_name', label: 'المنفّذ', default: true },
  { key: 'status', label: 'الحالة', default: true },
  { key: 'progress', label: 'الإنجاز', default: true },
  { key: 'last_completed_step', label: 'آخر خطوة مكتملة', default: true },
  { key: 'priority', label: 'الأولوية', default: false },
  { key: 'tags', label: 'الوسوم', default: true },
  { key: 'created_at', label: 'تاريخ الإنشاء', default: false },
  { key: 'start_date', label: 'تاريخ البدء', default: false },
  { key: 'due_date', label: 'تاريخ الاستحقاق', default: true },
  { key: 'completed_at', label: 'تاريخ الإنجاز', default: false },
  { key: 'logged_minutes', label: 'وقت العمل (د)', default: false },
];

export default function SheikhReport() {
  const { id } = useParams();
  const { api } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [filters, setFilters] = useState({
    month: '',
    from: '',
    to: '',
    status: '',
    project_id: searchParams.get('project_id') || '',
    sponsor_id: '',
    tag_ids: [],
  });

  const defaultColumns = useMemo(
    () => ALL_COLUMNS.filter(c => c.default).map(c => c.key),
    []
  );
  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  const [groupBy, setGroupBy] = useState('none');
  const [showColumnsPanel, setShowColumnsPanel] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const buildQs = useCallback(() => {
    const qs = new URLSearchParams();
    if (filters.month) qs.set('month', filters.month);
    else {
      if (filters.from) qs.set('from', filters.from);
      if (filters.to) qs.set('to', filters.to);
    }
    if (filters.status) qs.set('status', filters.status);
    if (filters.project_id) qs.set('project_id', filters.project_id);
    if (filters.sponsor_id) qs.set('sponsor_id', filters.sponsor_id);
    if (filters.tag_ids.length) qs.set('tag_ids', filters.tag_ids.join(','));
    return qs;
  }, [filters]);

  const load = useCallback(() => {
    setLoading(true);
    api(`/reports/sheikh/${id}?${buildQs()}`)
      .then(setData)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [id, buildQs, api, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api('/reports/months').then(setMonths).catch(() => {});
    api('/projects').then(setProjects).catch(() => {});
    api('/tags').then(setAllTags).catch(() => {});
    api('/report-templates?scope=sheikh').then(setTemplates).catch(() => {});
  }, [api]);

  const tasks = data?.tasks || [];

  const enrichedRows = useMemo(() => tasks.map(t => ({
    id: t.id,
    title: t.title,
    project_title: t.project?.title || '—',
    sponsor_name: t.project?.sponsor?.name || '—',
    assignee_name: t.assignee?.name || '—',
    status: STATUS_AR[t.status] || t.status,
    statusKey: t.status,
    progress: t.steps_total ? `${t.progress_percent}% (${t.steps_done}/${t.steps_total})` : `${t.progress_percent ?? 0}%`,
    last_completed_step: t.last_completed_step || '—',
    priority: t.priority,
    tags: t.tags || [],
    created_at: t.created_at,
    start_date: t.start_date,
    due_date: t.due_date,
    completed_at: t.completed_at,
    logged_minutes: t.logged_minutes || 0,
  })), [tasks]);

  const groupedRows = useMemo(() => {
    if (groupBy === 'none') return [{ key: '', label: '', rows: enrichedRows }];
    const groups = {};
    enrichedRows.forEach(r => {
      let key, label;
      if (groupBy === 'project') {
        key = r.project_title; label = r.project_title;
      } else if (groupBy === 'status') {
        key = r.status; label = r.status;
      } else if (groupBy === 'tag') {
        if (!r.tags.length) {
          key = '__none__'; label = 'بدون وسم';
          groups[key] = groups[key] || { key, label, rows: [] };
          groups[key].rows.push(r);
          return;
        }
        r.tags.forEach(t => {
          const k = `tag-${t.id}`;
          groups[k] = groups[k] || { key: k, label: t.name, rows: [] };
          groups[k].rows.push(r);
        });
        return;
      }
      groups[key] = groups[key] || { key, label, rows: [] };
      groups[key].rows.push(r);
    });
    return Object.values(groups);
  }, [enrichedRows, groupBy]);

  const toggleColumn = (key) => {
    setVisibleColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleTag = (tid) => {
    setFilters(f => ({
      ...f,
      tag_ids: f.tag_ids.includes(tid) ? f.tag_ids.filter(x => x !== tid) : [...f.tag_ids, tid],
    }));
  };

  const formatCell = (row, key) => {
    if (key === 'tags') return <TagList tags={row.tags || []} />;
    if (['created_at', 'start_date', 'due_date', 'completed_at'].includes(key)) {
      return row[key] ? fmtDate(row[key]) : '—';
    }
    if (key === 'status') return <span className={`badge badge-${row.statusKey}`}>{row[key]}</span>;
    if (key === 'logged_minutes') return fmtMinutes(row[key]);
    return row[key] ?? '—';
  };

  const printCurrent = () => {
    const cols = ALL_COLUMNS.filter(c => visibleColumns.includes(c.key));
    const rows = enrichedRows.map(r => {
      const out = { ...r };
      out.tags = (r.tags || []).map(t => t.name).join('، ');
      out.logged_minutes = fmtMinutes(r.logged_minutes);
      ['created_at', 'start_date', 'due_date', 'completed_at'].forEach(k => {
        if (r[k]) out[k] = fmtDate(r[k]);
      });
      return out;
    });
    printReport({
      title: `تقرير الشيخ: ${data?.sheikh?.name || ''}`,
      subtitle: rangeLabel(),
      columns: cols.map(c => ({ key: c.key, label: c.label })),
      rows,
    });
  };

  const downloadCsv = () => {
    const cols = ALL_COLUMNS.filter(c => visibleColumns.includes(c.key));
    const lines = [cols.map(c => c.label).join(',')];
    enrichedRows.forEach(r => {
      const line = cols.map(c => {
        let v = r[c.key];
        if (c.key === 'tags') v = (r.tags || []).map(t => t.name).join('؛ ');
        if (c.key === 'logged_minutes') v = fmtMinutes(r.logged_minutes);
        if (['created_at', 'start_date', 'due_date', 'completed_at'].includes(c.key)) {
          v = r[c.key] ? fmtDate(r[c.key]) : '';
        }
        if (v == null) v = '';
        v = String(v).replace(/"/g, '""');
        return `"${v}"`;
      }).join(',');
      lines.push(line);
    });
    const csv = '﻿' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sheikh-${data?.sheikh?.name || id}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('أدخل اسماً للقالب');
      return;
    }
    try {
      await api('/report-templates', {
        method: 'POST',
        body: JSON.stringify({
          name: templateName,
          scope: 'sheikh',
          config: { columns: visibleColumns, group_by: groupBy, filters },
        }),
      });
      toast.success('تم حفظ القالب');
      setTemplateName('');
      api('/report-templates?scope=sheikh').then(setTemplates).catch(() => {});
    } catch (err) { toast.error(err.message); }
  };

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    if (tpl.config?.columns) setVisibleColumns(tpl.config.columns);
    if (tpl.config?.group_by) setGroupBy(tpl.config.group_by);
    if (tpl.config?.filters) setFilters(prev => ({ ...prev, ...tpl.config.filters, tag_ids: tpl.config.filters.tag_ids || [] }));
    toast.success(`تم تطبيق القالب: ${tpl.name}`);
  };

  const rangeLabel = () => {
    if (filters.month) {
      const m = months.find(mm => mm.value === filters.month);
      return m ? m.label : filters.month;
    }
    if (filters.from || filters.to) return `من ${filters.from || '—'} إلى ${filters.to || '—'}`;
    return 'كامل المدة';
  };

  if (loading && !data) return <div className="loader">جاري تحميل التقرير...</div>;

  return (
    <div>
      <Link to="/clients" style={{ fontSize: 13, color: 'var(--text-muted)', display: 'inline-block', marginBottom: 8 }}>← العملاء</Link>
      <div className="report-cover" style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
        <div className="report-cover-inner">
          <div className="report-cover-text">
            <div className="report-cover-kicker">تقرير الشيخ</div>
            <h1 className="report-cover-title">{data?.sheikh?.name}</h1>
            <div className="report-cover-period">{rangeLabel()}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 10 }}>الفلاتر</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <div>
            <label className="form-label">شهر مغلق</label>
            <select className="form-select" value={filters.month}
              onChange={e => setFilters(f => ({ ...f, month: e.target.value, from: '', to: '' }))}>
              <option value="">— مدى مخصّص —</option>
              {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">من تاريخ</label>
            <input className="form-input" type="date" disabled={!!filters.month}
              value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
          </div>
          <div>
            <label className="form-label">إلى تاريخ</label>
            <input className="form-input" type="date" disabled={!!filters.month}
              value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
          </div>
          <div>
            <label className="form-label">المشروع</label>
            <select className="form-select" value={filters.project_id}
              onChange={e => setFilters(f => ({ ...f, project_id: e.target.value }))}>
              <option value="">كل المشاريع</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">الحالة</label>
            <select className="form-select" value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
              <option value="">كل الحالات</option>
              {Object.entries(STATUS_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">تجميع حسب</label>
            <select className="form-select" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="none">بدون تجميع</option>
              <option value="project">مشروع</option>
              <option value="status">حالة</option>
              <option value="tag">وسم</option>
            </select>
          </div>
        </div>
        {allTags.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <label className="form-label">الوسوم</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {allTags.map(t => (
                <button
                  key={t.id} type="button"
                  onClick={() => toggleTag(t.id)}
                  className={`btn btn-sm ${filters.tag_ids.includes(t.id) ? 'btn-primary' : 'btn-outline'}`}
                  style={{ background: filters.tag_ids.includes(t.id) ? t.color : undefined, borderColor: t.color, color: filters.tag_ids.includes(t.id) ? '#fff' : t.color }}
                >{t.name}</button>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={load}>🔄 تحديث</button>
          <button className="btn btn-outline btn-sm" onClick={() => setShowColumnsPanel(s => !s)}>
            📑 الأعمدة ({visibleColumns.length})
          </button>
          <button className="btn btn-outline btn-sm" onClick={printCurrent}>🖨 طباعة / PDF</button>
          <button className="btn btn-outline btn-sm" onClick={downloadCsv}>📥 CSV</button>
          {templates.length > 0 && (
            <select
              className="form-select"
              style={{ maxWidth: 200 }}
              onChange={e => {
                const t = templates.find(x => x.id === parseInt(e.target.value, 10));
                applyTemplate(t);
                e.target.value = '';
              }}
              defaultValue=""
            >
              <option value="">📋 تطبيق قالب...</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' ★' : ''}</option>)}
            </select>
          )}
        </div>
        {showColumnsPanel && (
          <div style={{ marginTop: 12, padding: 10, background: 'var(--gray-50, var(--surface-alt))', borderRadius: 6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
              {ALL_COLUMNS.map(c => (
                <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={visibleColumns.includes(c.key)} onChange={() => toggleColumn(c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
              <input
                className="form-input"
                style={{ maxWidth: 200 }}
                placeholder="اسم القالب لحفظه..."
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
              />
              <button className="btn btn-sm btn-primary" onClick={saveAsTemplate} disabled={!templateName.trim()}>💾 حفظ كقالب</button>
            </div>
          </div>
        )}
      </div>

      {data && (
        <div className="card" style={{ padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
            <Stat label="إجمالي المهام" value={data.totals.tasks} />
            <Stat label="مكتملة" value={data.totals.completed} color="var(--success)" />
            <Stat label="نشطة" value={data.totals.active} color="var(--primary)" />
            <Stat label="متأخرة" value={data.totals.overdue} color="var(--danger)" />
            <Stat label="وقت العمل" value={fmtMinutes(data.totals.logged_minutes)} />
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title" style={{ marginBottom: 12 }}>المهام ({tasks.length})</div>
        {tasks.length === 0 ? (
          <div className="empty">لا توجد مهام مطابقة</div>
        ) : (
          groupedRows.map(group => (
            <div key={group.key || 'all'} style={{ marginBottom: groupBy !== 'none' ? 16 : 0 }}>
              {groupBy !== 'none' && (
                <div style={{ fontWeight: 600, fontSize: 14, padding: '8px 0', borderBottom: '2px solid var(--primary)', marginBottom: 8 }}>
                  {group.label || '— بدون —'} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12 }}>({group.rows.length})</span>
                </div>
              )}
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>{ALL_COLUMNS.filter(c => visibleColumns.includes(c.key)).map(c => <th key={c.key}>{c.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {group.rows.map(r => (
                      <tr key={r.id}>
                        {ALL_COLUMNS.filter(c => visibleColumns.includes(c.key)).map(c => (
                          <td key={c.key}>{formatCell(r, c.key)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', padding: 10 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}
